import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AutoAwesome,
  AttachFile,
  HistoryEdu,
  Groups,
  TaskAlt,
  FolderCopy,
} from '@mui/icons-material';

import { IContract } from '../../../../../../types/contract';
import {
  ContractStatus,
  AIReadingStatus,
} from '../../../../../../types/contract';
import { DialogMode } from '../../../../../../types/dialogmode';
import { ISignee, ISignature } from '../../../../../../types/signature';
import { IDocument } from '../../../../../../types/document';
import useAuth from '../../../../../../hooks/useAuth';
import { useParams } from 'react-router-dom';

import AIContractSigneeManagement from '../../AIContractSigneeManagement';
import ContractAttachments from '../../ContractAttachments';
import ContractNotes from '../../ContractNotes';
import SidebarRiskAnalysis from '../../SidebarRiskAnalysis';
import SidebarRoles from '../../SidebarRoles';
import ContractFolders from '../../ContractFolders';

interface ContractManagementTabProps {
  contract: IContract;
  mode: DialogMode;
  signer?: ISignee;
  signees?: ISignee[];
  signatures?: ISignature[];
  document: IDocument;
  analysisStatus?: AIReadingStatus;
  onAnalysis?: () => void;
  setSignees?: React.Dispatch<React.SetStateAction<Array<ISignee>>>;
  setSignatures?: React.Dispatch<React.SetStateAction<Array<ISignature>>>;
  resetSignatures?: React.Dispatch<React.SetStateAction<boolean>>;
  setBadge: (tabIndex: string, num: number) => void;
  badges: { [key: string]: number };
  isDownLg: boolean;
}

const ContractManagementTab: React.FC<ContractManagementTabProps> = ({
  contract,
  mode,
  signer,
  signees,
  signatures,
  document,
  analysisStatus,
  onAnalysis,
  setSignees,
  setSignatures,
  resetSignatures,
  setBadge,
  badges,
  isDownLg,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { status } = useParams();

  return (
    <Stack>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('Contract management')}
      </Typography>
      <Accordion disabled>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-airead"
          id="panel-airead-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon>
              <AutoAwesome />
            </ListItemIcon>
            <ListItemText primary={t('AI_Analysis')} />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <SidebarRiskAnalysis
            contract={contract}
            document={document}
            isDownLg={isDownLg}
            analyzing={analysisStatus === AIReadingStatus.ANALYZING}
            onAnalysis={onAnalysis}
            isAnalyzed={!!contract?.analyzeStatus}
            hideTitle
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-attachments"
          id="panel-attachments-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon>
              <Badge badgeContent={badges.attachments} color="info" showZero>
                <AttachFile />
              </Badge>
            </ListItemIcon>
            <ListItemText primary={t('Attachments')} />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractAttachments
            mode={DialogMode.Edit}
            contract={contract}
            hideTitle
            setBadgeCount={(num: number) => setBadge('attachments', num)}
            contractWasAnalysed={true}
          />
        </AccordionDetails>
      </Accordion>
      {setSignees &&
        setSignatures &&
        resetSignatures &&
        signatures &&
        signees &&
        signer && (
          <Accordion disabled>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-signees"
              id="panel-signees-header"
            >
              <ListItem disablePadding disableGutters>
                <ListItemIcon>
                  <HistoryEdu />
                </ListItemIcon>
                <ListItemText primary={t('Signees')} />
              </ListItem>
            </AccordionSummary>
            <AccordionDetails>
              <AIContractSigneeManagement
                mode={
                  status === ContractStatus.SIGNED ||
                  status === ContractStatus.ACTIVE ||
                  status === ContractStatus.ARCHIVED
                    ? DialogMode.ViewOnly
                    : DialogMode.Edit
                }
                signer={signer}
                readOnly={false}
                contract={contract}
                signable={false}
                signees={signees}
                setSignees={setSignees}
                setSignatures={setSignatures}
                signatures={signatures}
                resetSignatures={resetSignatures}
                document={document}
                hideTitle
              />
            </AccordionDetails>
          </Accordion>
        )}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-roles"
          id="panel-roles-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon>
              <Groups />
            </ListItemIcon>
            <ListItemText primary={t('Roles')} />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <SidebarRoles
            contract={contract}
            hideTitle
            readonly={mode === DialogMode.ViewOnly}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-tasks"
          id="panel-tasks-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon>
              <Badge badgeContent={badges.tasks} color="info" showZero>
                <TaskAlt />
              </Badge>
            </ListItemIcon>
            <ListItemText primary={t('Notes')} />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractNotes
            contract={contract}
            hideTitle
            setBadgeCount={(num: number) => setBadge('tasks', num)}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-folders"
          id="panel-folders-header"
        >
          <ListItem disablePadding disableGutters>
            <ListItemIcon>
              <FolderCopy />
            </ListItemIcon>
            <ListItemText primary={t('Folders')} />
          </ListItem>
        </AccordionSummary>
        <AccordionDetails>
          <ContractFolders contract={contract} hideTitle />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default ContractManagementTab;
